import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { collection, onSnapshot } from "firebase/firestore";
import { getDownloadURL, ref as storageRefFromPath } from "firebase/storage";
import { getFunctions, httpsCallable } from "firebase/functions";
import { Link } from "react-router-dom";
import { AlertTriangle } from "lucide-react";

import CountryFlag from "../../components/common/CountryFlag";
import { auth, db, storage } from "../../firebase";
import { defaultAvatarByGender } from "../../utils/defaultAvatar";

const functions = getFunctions(undefined, "us-central1");

function toNumber(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function normalize(s) {
  return String(s ?? "").trim().toLowerCase();
}

function callableErrorMessage(error) {
  const code = error?.code ? String(error.code) : "";
  const msg = error?.message ? String(error.message) : "";
  if (code && msg) return `${code}: ${msg}`;
  return msg || "Request failed.";
}

/** Prefer photoURL per admin UI spec; fall back to profileImage */
function pickPhotoRaw(user) {
  const raw = user?.photoURL || user?.profileImage || "";
  return typeof raw === "string" ? raw.trim() : "";
}

function pickCountryRaw(user) {
  const c =
    user?.displayCountry ||
    user?.realCountry ||
    user?.country ||
    "";
  const s = typeof c === "string" ? c.trim() : "";
  return s;
}

function pickDisplayName(user) {
  const n = user?.displayName || user?.name || "";
  const s = typeof n === "string" ? n.trim() : "";
  return s || "—";
}

function UserAvatar({ raw, gender }) {
  const fb = defaultAvatarByGender(gender);
  const [src, setSrc] = useState(fb);

  useEffect(() => {
    let cancelled = false;
    const fallback = defaultAvatarByGender(gender);
    const v = String(raw || "").trim();
    if (!v) {
      setSrc(fallback);
      return () => {
        cancelled = true;
      };
    }
    if (/^https?:\/\//i.test(v)) {
      setSrc(v);
      return () => {
        cancelled = true;
      };
    }
    let path = v;
    if (v.startsWith("gs://")) {
      const m = v.match(/^gs:\/\/[^/]+\/(.+)$/);
      path = m ? m[1] : "";
    }
    if (!path) {
      setSrc(fallback);
      return () => {
        cancelled = true;
      };
    }
    (async () => {
      try {
        const url = await getDownloadURL(storageRefFromPath(storage, path));
        if (!cancelled) setSrc(url);
      } catch (e) {
        if (!cancelled) setSrc(fallback);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [raw, gender]);

  return (
    <img
      src={src}
      alt=""
      className="admin1-users__avatar"
      onError={(e) => {
        e.currentTarget.onerror = null;
        e.currentTarget.src = defaultAvatarByGender(gender);
      }}
    />
  );
}

const SUSPEND_REASON_MAX = 300;

function AdminSuspendConfirmModal({
  targetLine,
  reason,
  onReasonChange,
  onClose,
  onConfirm,
  working,
  t,
}) {
  const taRef = useRef(null);

  useEffect(() => {
    const id = window.requestAnimationFrame(() => {
      taRef.current?.focus();
    });
    return () => window.cancelAnimationFrame(id);
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape" && !working) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [working, onClose]);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  const n = reason.length;

  return (
    <div className="admin1-suspend-modal" role="presentation">
      <button
        type="button"
        className="admin1-suspend-modal__backdrop"
        aria-label={t("common.close", { defaultValue: "Close" })}
        disabled={working}
        onClick={() => {
          if (!working) onClose();
        }}
      />
      <div
        className="admin1-suspend-modal__card"
        role="dialog"
        aria-modal="true"
        aria-labelledby="admin1-suspend-modal-title"
      >
        <div className="admin1-suspend-modal__header">
          <span className="admin1-suspend-modal__icon-wrap" aria-hidden>
            <AlertTriangle size={26} strokeWidth={2} />
          </span>
          <div className="admin1-suspend-modal__header-text">
            <h2 id="admin1-suspend-modal-title" className="admin1-suspend-modal__title">
              {t("admin.users.suspend_account", { defaultValue: "Suspend account" })}
            </h2>
            <p className="admin1-suspend-modal__subtitle">
              {t("admin.users.suspend_modal_subtitle", {
                defaultValue:
                  "The user will be signed out and blocked from the app until unsuspended.",
              })}
            </p>
          </div>
        </div>
        <p className="admin1-suspend-modal__target">{targetLine}</p>
        <label className="admin1-suspend-modal__label" htmlFor="admin1-suspend-reason">
          {t("admin.users.suspend_reason_label", {
            defaultValue: "Reason (optional)",
          })}
        </label>
        <textarea
          id="admin1-suspend-reason"
          ref={taRef}
          className="admin1-suspend-modal__textarea"
          rows={4}
          maxLength={SUSPEND_REASON_MAX}
          value={reason}
          onChange={(e) => onReasonChange(e.target.value.slice(0, SUSPEND_REASON_MAX))}
          placeholder={t("admin.users.suspend_reason_placeholder", {
            defaultValue: "Add context for the audit log…",
          })}
          disabled={working}
          autoComplete="off"
        />
        <div className="admin1-suspend-modal__counter" aria-live="polite">
          {n} / {SUSPEND_REASON_MAX}
        </div>
        <div className="admin1-suspend-modal__footer">
          <button
            type="button"
            className="admin1-suspend-modal__btn admin1-suspend-modal__btn--cancel"
            disabled={working}
            onClick={() => {
              if (!working) onClose();
            }}
          >
            {t("common.cancel", { defaultValue: "Cancel" })}
          </button>
          <button
            type="button"
            className="admin1-suspend-modal__btn admin1-suspend-modal__btn--danger"
            disabled={working}
            onClick={() => void onConfirm()}
          >
            {working
              ? "…"
              : t("admin.users.suspend_confirm", { defaultValue: "Suspend account" })}
          </button>
        </div>
      </div>
    </div>
  );
}

function CopyUidButton({ uid }) {
  const [done, setDone] = useState(false);

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(uid);
      setDone(true);
      window.setTimeout(() => setDone(false), 1600);
    } catch (e) {
      console.error("clipboard:", e);
    }
  }

  return (
    <button
      type="button"
      className="admin1-users__copy"
      onClick={() => void handleCopy()}
    >
      {done ? "Copied" : "Copy"}
    </button>
  );
}

export default function UsersPage() {
  const { t } = useTranslation();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [genderFilter, setGenderFilter] = useState("all");
  const [verificationFilter, setVerificationFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [workingUid, setWorkingUid] = useState(null);
  const [feedbackByUid, setFeedbackByUid] = useState({});
  const [suspendModal, setSuspendModal] = useState(null);
  const [suspendReasonDraft, setSuspendReasonDraft] = useState("");

  useEffect(() => {
    setLoading(true);
    const usersRef = collection(db, "users");
    const unsub = onSnapshot(
      usersRef,
      (snap) => {
        const list = snap.docs.map((docSnap) => {
          const data = docSnap.data() || {};
          return {
            uid: docSnap.id,
            displayName: pickDisplayName(data),
            gender: String(data.gender ?? "—"),
            verificationStatus: String(data.verificationStatus ?? "—"),
            accountStatus: String(data.accountStatus ?? "active").trim().toLowerCase(),
            coins: toNumber(data.coins ?? data.coinsBalance, 0),
            availableTokens: toNumber(data?.earnings?.availableTokens, 0),
            online: Boolean(data.online),
            countryCode: pickCountryRaw(data),
            photoRaw: pickPhotoRaw(data),
          };
        });
        setRows(list);
        setLoading(false);
      },
      (err) => {
        console.error("UsersPage users snapshot error:", err);
        setRows([]);
        setLoading(false);
      }
    );
    return () => unsub();
  }, []);

  const filtered = useMemo(() => {
    const term = normalize(search);
    return rows.filter((row) => {
      if (genderFilter !== "all") {
        if (normalize(row.gender) !== genderFilter) return false;
      }
      if (verificationFilter !== "all") {
        if (normalize(row.verificationStatus) !== verificationFilter) {
          return false;
        }
      }
      if (term) {
        const inName = normalize(row.displayName).includes(term);
        const inUid = normalize(row.uid).includes(term);
        if (!inName && !inUid) return false;
      }
      return true;
    });
  }, [rows, genderFilter, verificationFilter, search]);

  const sorted = useMemo(() => {
    return [...filtered].sort((a, b) =>
      a.displayName.localeCompare(b.displayName, undefined, {
        sensitivity: "base",
      })
    );
  }, [filtered]);

  function openSuspendModal(uid, displayName) {
    const me = auth.currentUser;
    if (!me?.uid || uid === me.uid) return;
    setSuspendReasonDraft("");
    setSuspendModal({ uid, displayName: displayName || "—" });
  }

  async function confirmSuspendFromModal() {
    if (!suspendModal) return;
    const { uid } = suspendModal;
    const me = auth.currentUser;
    if (!me?.uid || uid === me.uid) return;

    const reason = suspendReasonDraft.trim().slice(0, SUSPEND_REASON_MAX);

    setWorkingUid(uid);
    setFeedbackByUid((prev) => {
      const next = { ...prev };
      delete next[uid];
      return next;
    });

    try {
      const fn = httpsCallable(functions, "adminSuspendUser");
      await fn({ targetUid: uid, reason });
    } catch (error) {
      console.error("adminSuspendUser failed:", error);
      setFeedbackByUid((prev) => ({
        ...prev,
        [uid]: callableErrorMessage(error),
      }));
    } finally {
      setWorkingUid(null);
      setSuspendModal(null);
    }
  }

  async function handleUnsuspend(uid) {
    const me = auth.currentUser;
    if (!me?.uid || uid === me.uid) return;

    setWorkingUid(uid);
    setFeedbackByUid((prev) => {
      const next = { ...prev };
      delete next[uid];
      return next;
    });

    try {
      const fn = httpsCallable(functions, "adminUnsuspendUser");
      await fn({ targetUid: uid });
    } catch (error) {
      console.error("adminUnsuspendUser failed:", error);
      setFeedbackByUid((prev) => ({
        ...prev,
        [uid]: callableErrorMessage(error),
      }));
    } finally {
      setWorkingUid(null);
    }
  }

  const nf = useMemo(() => new Intl.NumberFormat(), []);
  const adminUid = auth.currentUser?.uid;

  return (
    <div className="admin1-dashboard">
      <header className="admin1-dashboard__header">
        <h1 className="admin1-dashboard__title">{t("admin.users.title", { defaultValue: "Users" })}</h1>
        <p className="admin1-dashboard__subtitle">
          {t("admin.users.subtitle_suspend", {
            defaultValue:
              "Suspend account sets users.accountStatus to suspended and signs the user out on next app load. Unsuspend restores active.",
          })}
        </p>
      </header>

      <div className="admin1-users__toolbar">
        <label className="admin1-users__field">
          <span className="admin1-users__label">{t("admin.users.gender", { defaultValue: "Gender" })}</span>
          <select
            className="admin1-users__select"
            value={genderFilter}
            onChange={(e) => setGenderFilter(e.target.value)}
          >
            <option value="all">{t("common.all", { defaultValue: "All" })}</option>
            <option value="male">{t("gender.male", { defaultValue: "Male" })}</option>
            <option value="female">{t("gender.female", { defaultValue: "Female" })}</option>
          </select>
        </label>

        <label className="admin1-users__field">
          <span className="admin1-users__label">{t("admin.verification.title", { defaultValue: "Verification" })}</span>
          <select
            className="admin1-users__select"
            value={verificationFilter}
            onChange={(e) => setVerificationFilter(e.target.value)}
          >
            <option value="all">{t("common.all", { defaultValue: "All" })}</option>
            <option value="approved">{t("admin.status.approved", { defaultValue: "Approved" })}</option>
            <option value="pending">{t("admin.status.pending", { defaultValue: "Pending" })}</option>
            <option value="rejected">{t("admin.status.rejected", { defaultValue: "Rejected" })}</option>
          </select>
        </label>

        <label className="admin1-users__field admin1-users__field--grow">
          <span className="admin1-users__label">{t("common.search", { defaultValue: "Search" })}</span>
          <input
            className="admin1-users__input"
            type="search"
            placeholder={t("admin.users.search_placeholder", { defaultValue: "Name or UID" })}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            autoComplete="off"
          />
        </label>
      </div>

      {loading ? (
        <div className="admin1-state-card">Loading users…</div>
      ) : rows.length === 0 ? (
        <div className="admin1-state-card">{t("admin.users.empty", { defaultValue: "No users in Firestore." })}</div>
      ) : sorted.length === 0 ? (
        <div className="admin1-state-card">{t("admin.users.no_filter_results", { defaultValue: "No users match the filters." })}</div>
      ) : (
        <div className="admin1-users__table-wrap">
          <table className="admin1-users__table">
            <thead>
              <tr>
                <th />
                <th>{t("admin.users.name", { defaultValue: "Name" })}</th>
                <th>{t("admin.users.uid", { defaultValue: "UID" })}</th>
                <th>{t("admin.users.gender", { defaultValue: "Gender" })}</th>
                <th>{t("admin.verification.title", { defaultValue: "Verification" })}</th>
                <th>{t("admin.finance.coins", { defaultValue: "Coins" })}</th>
                <th>{t("admin.finance.available", { defaultValue: "Available" })}</th>
                <th>{t("admin.users.country", { defaultValue: "Country" })}</th>
                <th>{t("common.status", { defaultValue: "Status" })}</th>
                <th>{t("admin.users.account", { defaultValue: "Account" })}</th>
                <th>{t("common.actions", { defaultValue: "Actions" })}</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((row) => {
                const isSelf = adminUid === row.uid;
                const isSuspended = row.accountStatus === "suspended";
                const busy = workingUid === row.uid;
                const feedback = feedbackByUid[row.uid];
                const verified =
                  normalize(row.verificationStatus) === "approved";

                return (
                  <tr key={row.uid}>
                    <td>
                      <UserAvatar raw={row.photoRaw} gender={row.gender} />
                    </td>
                    <td className="admin1-users__name">{row.displayName}</td>
                    <td>
                      <div className="admin1-users__uid-cell">
                        <span className="admin1-users__mono admin1-users__mono--clip">
                          {row.uid}
                        </span>
                        <CopyUidButton uid={row.uid} />
                      </div>
                    </td>
                    <td>{row.gender}</td>
                    <td>
                      <div className="admin1-users__stack">
                        <span
                          className={
                            verified
                              ? "admin1-badge admin1-badge--verified"
                              : "admin1-badge admin1-badge--unverified"
                          }
                        >
                          {verified ? "Verified" : "Not verified"}
                        </span>
                        <span className="admin1-users__muted">
                          {row.verificationStatus}
                        </span>
                      </div>
                    </td>
                    <td>{nf.format(row.coins)}</td>
                    <td>{nf.format(row.availableTokens)}</td>
                    <td>
                      <CountryFlag
                        countryCode={row.countryCode}
                        className="admin1-users__country-flag"
                      />
                    </td>
                    <td>
                      <span
                        className={
                          row.online
                            ? "admin1-badge admin1-badge--online"
                            : "admin1-badge admin1-badge--offline"
                        }
                      >
                        {row.online ? "Online" : "Offline"}
                      </span>
                    </td>
                    <td>
                      {isSuspended ? (
                        <span className="admin1-badge admin1-badge--blocked">
                          {t("admin.users.suspended", { defaultValue: "Suspended" })}
                        </span>
                      ) : (
                        <span className="admin1-badge admin1-badge--clear">
                          {t("admin.users.active", { defaultValue: "Active" })}
                        </span>
                      )}
                    </td>
                    <td>
                      <div className="admin1-users__actions">
                        <Link
                          className="admin1-users__btn admin1-users__btn--primary"
                          to={`/admin1/user/${encodeURIComponent(row.uid)}`}
                        >
                          View Details
                        </Link>
                        <Link
                          className="admin1-users__btn admin1-users__btn--ghost"
                          to={`/user/${encodeURIComponent(row.uid)}`}
                          target="_blank"
                          rel="noreferrer"
                        >
                          Public profile
                        </Link>
                        {!isSelf ? (
                          <button
                            type="button"
                            className={
                              isSuspended
                                ? "admin1-users__btn admin1-users__btn--ok"
                                : "admin1-users__btn admin1-users__btn--danger"
                            }
                            disabled={busy}
                            onClick={() =>
                              isSuspended
                                ? handleUnsuspend(row.uid)
                                : openSuspendModal(row.uid, row.displayName)
                            }
                          >
                            {busy
                              ? "…"
                              : isSuspended
                              ? t("admin.users.unsuspend", { defaultValue: "Unsuspend" })
                              : t("admin.users.suspend_account", {
                                  defaultValue: "Suspend account",
                                })}
                          </button>
                        ) : null}
                      </div>
                      {feedback ? (
                        <div className="admin1-users__feedback">{feedback}</div>
                      ) : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {!loading && sorted.length > 0 ? (
        <p className="admin1-users__meta">
          Showing {sorted.length} of {rows.length} loaded users
        </p>
      ) : null}

      {suspendModal ? (
        <AdminSuspendConfirmModal
          targetLine={`${suspendModal.displayName} · ${suspendModal.uid}`}
          reason={suspendReasonDraft}
          onReasonChange={setSuspendReasonDraft}
          onClose={() => {
            if (workingUid === suspendModal?.uid) return;
            setSuspendModal(null);
          }}
          onConfirm={confirmSuspendFromModal}
          working={workingUid === suspendModal.uid}
          t={t}
        />
      ) : null}
    </div>
  );
}
